/*
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 *
 * Copyright 2024 UNESP Universidade Estadual Paulista "Júlio de Mesquita Filho"
 * Copyright 2024-2026 UNICAMP Universidade Estadual de Campinas
 *
 */

import { HttpClient } from '@angular/common/http'
import { Inject, Injectable } from '@angular/core'
import { UnespCoreEnvironment } from 'src/libs/unesp-core'
import { identity, Observable } from 'rxjs'
import { FormularioComplementar } from '../models/formulario-complementar'
import { FormularioReordenacao } from '../models/formulario-reordenacao'
import { ErrorHandlerService } from './error-handler.service'

@Injectable({
  providedIn: 'root',
})
export class FormularioComplementarService {
  private baseUrl: string

  constructor(
    private http: HttpClient,
    private errorHandlerService: ErrorHandlerService,
    @Inject('environment') env: UnespCoreEnvironment
  ) {
    this.baseUrl = env.baseUrlApi
  }

  listar(): Observable<FormularioComplementar[]> {
    return this.http
      .get<FormularioComplementar[]>(`${this.baseUrl}/formulario-complementar`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  get(id: number): Observable<FormularioComplementar> {
    return this.http
      .get<FormularioComplementar>(`${this.baseUrl}/formulario-complementar/${id}`)      
  }

  gravar(form: FormularioComplementar): Observable<FormularioComplementar> {
    return this.http
      .post<FormularioComplementar>(`${this.baseUrl}/formulario-complementar`, form)      
  }

  atualizar(id: number, form: FormularioComplementar): Observable<FormularioComplementar> {
    return this.http
      .put<FormularioComplementar>(`${this.baseUrl}/formulario-complementar/${id}`, form)      
  }

  excluir(id: number): Observable<FormularioComplementar> {
    return this.http
      .delete<FormularioComplementar>(`${this.baseUrl}/formulario-complementar/${id}`)
      .pipe(identity, this.errorHandlerService.catchError())
  }

  reordenar(reordenacao: FormularioReordenacao[]): Observable<number> {
    return this.http
      .post<number>(`${this.baseUrl}/formulario-complementar/reordenar`, reordenacao)
      .pipe(identity, this.errorHandlerService.catchError())
  }
}
